import { Link } from 'react-router-dom';

export default function SEOTextSection() {
  return (
    <section className="w-full py-16 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <article>
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
            What Makes a Great CSSBuy Sheet?
          </h2>

          <div className="prose prose-sm max-w-none text-foreground-600 space-y-4">
            <p className="leading-relaxed">
              Finding reliable sellers on shopping platforms is one of the biggest challenges for anyone using agents like CSSBuy. A well-organized <strong>CSSBuy sheet</strong> transforms hours of scattered forum digging into a streamlined shopping experience. Instead of jumping between Reddit threads, Discord servers, and Weidian links, a curated spreadsheet puts everything in one place.
            </p>

            <p className="leading-relaxed">
              The best CSSBuy spreadsheets share common traits: they are updated frequently, organized by product category, and include direct links to verified seller storefronts. Whether you are hunting for the latest sneaker releases, premium streetwear brands like Stussy or Essentials, or budget-friendly basics, a properly maintained spreadsheet saves both time and money by filtering out unreliable sellers before you ever place an order.
            </p>

            <p className="leading-relaxed">
              Our directory on <strong>CSSSpreadsheet.cam</strong> connects you directly to the most comprehensive CSSBuy spreadsheet resources available. Each category link has been hand-picked to ensure you are accessing spreadsheets that are actively maintained, contain legitimate seller links, and cover the full spectrum of products that shoppers actually want.
            </p>

            <h3 className="text-lg font-heading font-bold text-foreground-900 mt-8 mb-3">
              Why Use a Spreadsheet Instead of Searching Directly?
            </h3>

            <p className="leading-relaxed">
              Shopping agents like CSSBuy, Kakobuy, Superbuy, and Oopbuy give you access to Chinese marketplaces such as Weidian, Taobao, and 1688. However, these platforms are not designed for international browsing. Search interfaces are often clunky, product listings can be misleading, and finding reputable sellers requires knowledge of local rating systems and community reputation. A CSSBuy sheet acts as a bridge, pre-vetting sellers so you can shop with confidence.
            </p>

            <p className="leading-relaxed">
              Beyond just finding sellers, spreadsheets often include additional value: QC photo examples, sizing advice, shipping weight estimates, price comparisons across agents, and notes about seller communication styles. This contextual information turns a simple list of links into a complete shopping companion.
            </p>

            <h3 className="text-lg font-heading font-bold text-foreground-900 mt-8 mb-3">
              Explore Spreadsheets by Brand
            </h3>

            <p className="leading-relaxed">
              Our directory covers the most sought-after brands in the spreadsheet ecosystem. Browse our dedicated brand pages for in-depth guides on finding verified sellers for each label: <Link to="/brands/nike" className="text-accent-600 hover:text-accent-700 underline">Nike</Link> — the most comprehensive spreadsheet coverage of any brand, from Dunks to Jordans; <Link to="/brands/adidas" className="text-accent-600 hover:text-accent-700 underline">Adidas</Link> — Samba, Gazelle, Campus, and Yeezy sellers curated by model and tier; and <Link to="/brands/supreme" className="text-accent-600 hover:text-accent-700 underline">Supreme</Link> — box logo hoodies, tees, and accessories with detailed embroidery QC checklists.
            </p>

            <h3 className="text-lg font-heading font-bold text-foreground-900 mt-8 mb-3">
              Compare Shopping Agents
            </h3>

            <p className="leading-relaxed">
              Not sure which agent to use for your spreadsheet haul? We have broken down the differences between every major platform. Read <Link to="/comparison/cssbuy-vs-kakobuy" className="text-accent-600 hover:text-accent-700 underline">CSSBuy vs Kakobuy</Link> for a fee and interface comparison, <Link to="/comparison/cssbuy-vs-superbuy" className="text-accent-600 hover:text-accent-700 underline">CSSBuy vs Superbuy</Link> for a deep dive on warehouse QC and shipping depth, and <Link to="/comparison/kakobuy-vs-superbuy" className="text-accent-600 hover:text-accent-700 underline">Kakobuy vs Superbuy</Link> for the newcomer-versus-veteran analysis. Also check our agent-specific resource hubs for <Link to="/kakobuy-spreadsheet" className="text-accent-600 hover:text-accent-700 underline">Kakobuy</Link>, <Link to="/superbuy-spreadsheet" className="text-accent-600 hover:text-accent-700 underline">Superbuy</Link>, and <Link to="/oopbuy-spreadsheet" className="text-accent-600 hover:text-accent-700 underline">Oopbuy</Link>.
            </p>

            <h3 className="text-lg font-heading font-bold text-foreground-900 mt-8 mb-3">
              Getting Started with CSSBuy Sheets
            </h3>

            <p className="leading-relaxed">
              To begin using a CSSBuy sheet effectively, start with a category that matches your shopping goals. If you are looking for sneakers, navigate to our Shoes section where you will find spreadsheets organized by brand, model, and price tier. For streetwear, the Hoodies and Jackets categories offer curated seller lists with notes on sizing accuracy and material quality.
            </p>

            <p className="leading-relaxed">
              Each spreadsheet link opens on kaspreadsheet.com, where you can browse the full directory of verified sellers, copy product links into your CSSBuy agent account, and begin placing orders. The process is designed to be intuitive regardless of your experience level with shopping agents.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/cssbuy-sheet-guide"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Read the Full CSSBuy Sheet Guide
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
            <Link
              to="/guides/shipping"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-secondary-100 text-secondary-800 font-medium text-sm hover:bg-secondary-200 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-ship-line text-base"></i>
              Shipping Guide
            </Link>
            <Link
              to="/guides/qc"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-accent-100 text-accent-800 font-medium text-sm hover:bg-accent-200 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-eye-line text-base"></i>
              QC Guide
            </Link>
          </div>
        </article>
      </div>
    </section>
  );
}